'use client';

import React from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';

interface InfoCardProps {
  icon: React.ReactNode;
  iconColor: string;
  label: string;
  value?: string | React.ReactNode;
  valueColor?: string;
  subtext?: string | React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

const Card = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 24px;
  ${({ $clickable }) => $clickable && `
    cursor: pointer;
    transition: opacity 0.2s ease;
    &:hover {
      opacity: 0.8;
    }
  `}
`;

const IconContainer = styled.div<{ $color: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.gray[550]};
  font-family: 'Open Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0;
  line-height: 16px;
  margin-bottom: 3px;
`;

const Value = styled.div<{ $color?: string }>`
  font-size: 24px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 700;
  line-height: 33px;
  color: ${props => props.$color || theme.colors.gray[900]};
  margin-bottom: 1px;
  word-break: break-word;
  max-width: 400px;
`;

const Subtext = styled.div`
  font-size: 14px;
  color: ${theme.colors.gray[900]};
  line-height: 16px;
  max-width: 400px;
  word-break: break-word;
  
`;

export const InfoCard: React.FC<InfoCardProps> = ({
  icon,
  iconColor,
  label,
  value,
  valueColor,
  subtext,
  children,
  className,
  onClick,
}) => {
  return (
    <Card className={className} $clickable={!!onClick} onClick={onClick}>
      <IconContainer $color={iconColor}>
        {icon}
      </IconContainer>
      <Content>
        <Label>{label}</Label>
        {value && (typeof value === 'string' ? (
          <Value $color={valueColor}>
            {value}
          </Value>
        ) : (
          value
        ))}
        {subtext && <Subtext>{subtext}</Subtext>}
        {children}
      </Content>
    </Card>
  );
};

